// Cuanto se espera a una peticion antes de darla por perdida.
//
// ── EL PROBLEMA ────────────────────────────────────────────────────────────
//
// Lissette, 2026-09-02, con el sistema lento: "no todos los modulos estan
// cargando". No estaban rotos: estaban ESPERANDO. `fetch` no trae ningun
// limite de tiempo puesto, asi que una llamada que sale y no vuelve se queda
// girando hasta que el navegador se cansa —varios minutos— y mientras tanto
// el modulo se ve vacio: sin spinner, sin error, sin nada que explique nada.
//
// La diferencia que esto hace es entre "se quedo cargando y no se por que" y
// "esto tardo demasiado, vuelve a intentarlo".
//
// ── CUANTO SE AGUANTA ──────────────────────────────────────────────────────
//
// 45 segundos por intento para una llamada normal. No es poco a proposito:
// hay pantallas que hoy tardan de verdad (Pagos del Mes entre ellas) y un
// limite corto las romperia en vez de arreglarlas. Este numero baja cuando esa
// reforma este hecha, no antes.
//
// Y hay llamadas que legitimamente tardan mucho mas: subir un archivo,
// exportar el listado entero, importar una hoja, disparar un envio masivo.
// Esas se reconocen SOLAS —por el cuerpo (FormData) o por la ruta— porque
// api.js tiene mas de 500 llamadas y una lista escrita a mano se quedaria
// vieja el primer dia. Quien necesite otro numero lo pasa en `init.timeoutMs`;
// un 0 quita el limite del todo.

export const LIMITE_NORMAL_MS = 45000;
export const LIMITE_LARGO_MS = 240000;

// `send` cubre los envios masivos; `documents`/`attachments`/`upload` las
// subidas que no van por FormData. El `(\b|\/)` evita que "sender" o
// "importante" cuenten como ruta larga.
export const RUTAS_QUE_TARDAN =
  /\/(export|import|bulk|upload|uploads|attachments|documents|send)(\b|\/)/i;

export function limiteDeTiempoPara(path, init) {
  if (init && Object.prototype.hasOwnProperty.call(init, 'timeoutMs')) {
    const pedido = Number(init.timeoutMs);
    // Un 0 (o cualquier cosa que no sea un numero positivo) = sin limite.
    return Number.isFinite(pedido) && pedido > 0 ? pedido : 0;
  }
  const esSubida =
    typeof FormData !== 'undefined' && init && init.body instanceof FormData;
  if (esSubida || RUTAS_QUE_TARDAN.test(String(path || ''))) return LIMITE_LARGO_MS;
  return LIMITE_NORMAL_MS;
}

/**
 * Un reloj para UN intento, unido a la senal que ya traiga quien llama.
 *
 * Las dos tienen que poder cortar, y por motivos OPUESTOS:
 *   - la del llamante, porque el componente se desmonto o la busqueda quedo
 *     vieja. Eso no es un fallo: no se avisa y no se reintenta.
 *   - la del reloj, porque se acabo la paciencia. Eso si es un fallo y hay
 *     que decirlo en pantalla.
 *
 * Las dos llegan al `catch` como el mismo AbortError, asi que sin
 * `seAgotoElTiempo` no habria forma de distinguirlas — y confundirlas
 * significaria o callar un fallo real, o dar por fallida una pantalla que
 * simplemente se cerro.
 */
export function relojDeIntento(senalDelLlamante, ms) {
  const control = new AbortController();
  let agotado = false;
  const alAbortar = () => control.abort();
  const temporizador = ms > 0
    ? setTimeout(() => { agotado = true; control.abort(); }, ms)
    : null;
  if (senalDelLlamante) {
    if (senalDelLlamante.aborted) control.abort();
    else senalDelLlamante.addEventListener('abort', alAbortar, { once: true });
  }
  return {
    signal: control.signal,
    seAgotoElTiempo: () => agotado,
    // Hay que llamarlo SIEMPRE, tambien cuando el intento sale bien: un
    // temporizador de 45 s vivo por cada llamada de la sesion es una fuga, y
    // el oyente sobre la senal del llamante tambien.
    soltar() {
      if (temporizador) clearTimeout(temporizador);
      if (senalDelLlamante) senalDelLlamante.removeEventListener('abort', alAbortar);
    },
  };
}
